let originalTitle = typeof document !== 'undefined' ? document.title : '';
let titleInterval: NodeJS.Timeout | null = null;
let originalFavicon: string | null = null;
let listenersAttached = false;

export const requestNotificationPermission = () => {
  if (typeof window !== 'undefined') {
    // Disable entirely in development or admin routes
    const isDevelopment = process.env.NODE_ENV === 'development';
    const isProtectedArea = window.location.pathname.startsWith('/admin');
    if (isDevelopment || isProtectedArea) return;

    // If OneSignal is loaded, use its raw native prompt method
    if ((window as any).OneSignal) {
      (window as any).OneSignalDeferred = (window as any).OneSignalDeferred || [];
      (window as any).OneSignalDeferred.push(async (OneSignal: any) => {
        // Only use the native browser prompt, as requested by the user
        if (OneSignal.Notifications?.requestPermission) {
          await OneSignal.Notifications.requestPermission();
        } else if (OneSignal.showNativePrompt) {
          OneSignal.showNativePrompt();
        }
      });
    } else if ('Notification' in window && Notification.permission === 'default') {
      // Fallback to raw browser prompt if OneSignal isn't loaded
      Notification.requestPermission();
    }
  }
};

export const playCompletionAlerts = (title: string, message: string) => {
  if (typeof window === 'undefined') return;

  const isDevelopment = process.env.NODE_ENV === 'development';
  const isProtectedArea = window.location.pathname.startsWith('/admin');

  // 1. Tab Flashing
  if (!titleInterval) {
    originalTitle = document.title;
    let isFlashing = false;
    titleInterval = setInterval(() => {
      document.title = isFlashing ? originalTitle : `(1) ${title}`;
      isFlashing = !isFlashing;
    }, 500);
  }

  // 3. Favicon Canvas
  setFaviconBadge();

  if (!listenersAttached) {
    setupClearListeners();
  }
};

export const clearCompletionAlerts = () => {
  if (typeof window === 'undefined') return;

  // Clear Tab Flashing
  if (titleInterval) {
    clearInterval(titleInterval);
    titleInterval = null;
    document.title = originalTitle;
  }

  // Clear Favicon
  clearFaviconBadge();
  listenersAttached = false;
};

const setFaviconBadge = () => {
  const link =
    (document.querySelector("link[rel~='icon']") as HTMLLinkElement) ||
    document.createElement('link');
  if (!link.parentNode) {
    link.rel = 'icon';
    document.head.appendChild(link);
  }

  if (!originalFavicon) {
    originalFavicon = link.href || '/favicon.ico'; // Fallback
  }

  const img = new Image();
  img.crossOrigin = 'anonymous';
  img.onload = () => {
    const canvas = document.createElement('canvas');
    canvas.width = 32;
    canvas.height = 32;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.drawImage(img, 0, 0, 32, 32);

    // Draw badge background
    ctx.beginPath();
    ctx.arc(24, 24, 8, 0, 2 * Math.PI); // bottom right corner
    ctx.fillStyle = '#ef4444'; // Red
    ctx.fill();

    // Draw checkmark
    ctx.fillStyle = '#ffffff';
    ctx.font = 'bold 10px Arial';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('✓', 24, 25);

    link.href = canvas.toDataURL('image/png');
  };
  img.src = originalFavicon;
};

const clearFaviconBadge = () => {
  const link = document.querySelector("link[rel~='icon']") as HTMLLinkElement;
  if (link && originalFavicon) {
    link.href = originalFavicon;
    originalFavicon = null;
  }
};

const setupClearListeners = () => {
  const clear = () => {
    clearCompletionAlerts();
    document.removeEventListener('visibilitychange', handleVisibility);
    document.removeEventListener('click', clear);
    document.removeEventListener('keydown', clear);
    document.removeEventListener('focus', clear);
  };

  const handleVisibility = () => {
    if (document.visibilityState === 'visible') {
      clear();
    }
  };

  document.addEventListener('visibilitychange', handleVisibility);
  document.addEventListener('click', clear);
  document.addEventListener('keydown', clear);
  window.addEventListener('focus', clear);
  listenersAttached = true;
};
